import {
  Award,
  Bookmarks,
  Design1,
  Explore,
  Eye,
  RecipeBook,
  Replay,
  ShapeStar,
  Sparkles,
  UserFrame,
} from '@/client/components/icons/ContinuIcons';

import { ContentTypes } from '@/client/services/api/graphql/gql/graphql';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useAuthStore } from '@/client/services/state/authStore';
import { useGetDashboardUserAssignments } from '@/client/components/learners/dashboard/hooks/useGetUserAssignments';
import { useGetUserWorkshops } from '@/client/components/learners/dashboard/hooks/useGetUserWorkshops';
import { useTranslation } from 'react-i18next';

export const useLearnerDashboard = () => {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { is_purchaser: isPurchaser } = user;
  const { allowance } = useAccessHelper();
  const { recommendations } = allowance;

  const { data: assignments } = useGetDashboardUserAssignments();

  const { data: workshops } = useGetUserWorkshops();

  const navItems = [
    {
      label: t('learners.dashboard.nav.dashboard'),
      icon: Explore,
      route: '/dashboard',
    },
    {
      label: t('learners.dashboard.nav.assignments'),
      icon: RecipeBook,
      route: 'assignments',
      count: assignments?.openAssignments.length,
    },
    {
      label: t('learners.dashboard.nav.bookmarks'),
      icon: Bookmarks,
      route: 'bookmarks',
    },
    {
      label: t('learners.dashboard.nav.recentlyViewed'),
      icon: Eye,
      route: 'recently-viewed',
    },
    {
      label: t('learners.dashboard.nav.workshops'),
      icon: Design1,
      route: 'workshops',
      count: workshops?.registered.length,
    },
    {
      label: t('learners.dashboard.nav.achievements'),
      icon: Award,
      route: 'achievements',
    },
    // {
    //   label: t('learners.dashboard.nav.editProfile'),
    //   icon: UserFrame,
    //   route: '/settings',
    // },
  ];

  if (isPurchaser) {
    navItems.splice(navItems.length - 1, 0, {
      label: t('learners.dashboard.nav.subscriptions'),
      icon: Replay,
      route: 'subscriptions',
    });
  }

  if (recommendations) {
    navItems.splice(4, 0, {
      label: t('learners.dashboard.nav.recommendations'),
      icon: Sparkles,
      route: 'recommendations',
    });
  }

  const contentTypeTranslationMap: Record<ContentTypes | string, string> = {
    [ContentTypes.Article]: t('learners.dashboard.contentTypes.article'),
    article: t('learners.dashboard.contentTypes.article'),
    [ContentTypes.Assessment]: t('learners.dashboard.contentTypes.assessment'),
    assessment: t('learners.dashboard.contentTypes.assessment'),
    [ContentTypes.Event]: t('learners.dashboard.contentTypes.event'),
    event: t('learners.dashboard.contentTypes.event'),
    [ContentTypes.File]: t('learners.dashboard.contentTypes.file'),
    file: t('learners.dashboard.contentTypes.file'),
    [ContentTypes.ImportedContent]: t('learners.dashboard.contentTypes.importedContent'),
    imported_content: t('learners.dashboard.contentTypes.importedContent'),
    [ContentTypes.Journey]: t('learners.dashboard.contentTypes.journey'),
    journey: t('learners.dashboard.contentTypes.journey'),
    [ContentTypes.Media]: t('learners.dashboard.contentTypes.media'),
    media: t('learners.dashboard.contentTypes.media'),
    [ContentTypes.MultiSessionWorkshop]: t('learners.dashboard.contentTypes.multiSessionWorkshop'),
    multi_session_workshop: t('learners.dashboard.contentTypes.multiSessionWorkshop'),
    [ContentTypes.MultiSessionWorkshopSession]: t(
      'learners.dashboard.contentTypes.multiSessionWorkshopSession',
    ),
    multi_session_workshop_session: t(
      'learners.dashboard.contentTypes.multiSessionWorkshopSession',
    ),
    [ContentTypes.Poll]: t('learners.dashboard.contentTypes.poll'),
    poll: t('learners.dashboard.contentTypes.poll'),
    [ContentTypes.Presentation]: t('learners.dashboard.contentTypes.presentation'),
    presentation: t('learners.dashboard.contentTypes.presentation'),
    [ContentTypes.Quiz]: t('learners.dashboard.contentTypes.quiz'),
    quiz: t('learners.dashboard.contentTypes.quiz'),
    [ContentTypes.Scorm]: t('learners.dashboard.contentTypes.scorm'),
    scorm: t('learners.dashboard.contentTypes.scorm'),
    [ContentTypes.Track]: t('learners.dashboard.contentTypes.track'),
    track: t('learners.dashboard.contentTypes.track'),
    [ContentTypes.Video]: t('learners.dashboard.contentTypes.video'),
    video: t('learners.dashboard.contentTypes.video'),
    [ContentTypes.Workshop]: t('learners.dashboard.contentTypes.workshop'),
    workshop: t('learners.dashboard.contentTypes.workshop'),
  };

  return { navItems, contentTypeTranslationMap };
};
